import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './GlobalSearch.css';
import ImagenProducto from './ImagenProducto';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const MAX_PER_GROUP = 5;

function fmtMoney(n) {
  if (!n) return '—';
  return `$${parseFloat(n).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
}

const ORDER_STATUS = {
  completed:  'Completado',
  processing: 'Pagado · Por preparar',
  pending:    'Pendiente de pago',
  'on-hold':  'Pago por confirmar',
  cancelled:  'Cancelado',
  refunded:   'Reembolsado',
};

/* Estado por sección, leído del STATUS de la respuesta.
   El rol lo decide el backend (`server.js:1103` gatea /api/customers con
   requireRole('admin','coordinador')). Este componente NO re-escribe esa condición —
   no pregunta por user.role, reacciona al 403. Duplicar el criterio del guard en el
   cliente es exactamente cómo un guard se desincroniza en silencio. */
function sectionStatus(res) {
  if (res.ok) return 'ok';
  return (res.status === 401 || res.status === 403) ? 'denied' : 'error';
}

const ALL_OK = { orders: 'ok', customers: 'ok' };

const SECTION_LABEL = { orders: 'pedidos', customers: 'clientes' };

/* Las secciones que quedaron en un estado dado, en castellano y ya unidas.
   Nombrar cuáles importa: «no pudimos buscar pedidos y clientes» sobre un fallo que
   sólo afectó a una de las dos es una afirmación de más. */
function sectionsWith(status, estado) {
  return Object.keys(SECTION_LABEL).filter((k) => status[k] === estado).map((k) => SECTION_LABEL[k]);
}

function listar(nombres) {
  return nombres.length === 2 ? `${nombres[0]} y ${nombres[1]}` : nombres[0];
}

function filterProducts(products, query) {
  if (!query.trim()) return [];
  const q = query.toLowerCase();
  return products.filter(
    (p) =>
      p.name.toLowerCase().includes(q) ||
      p.categories.some((c) => c.toLowerCase().includes(q)) ||
      (p.variations || []).some((v) => v.label?.toLowerCase().includes(q))
  ).slice(0, MAX_PER_GROUP);
}

/* Rol (fijado por Miguel 2026-07-22): GLOBAL = "llévame a ESTE registro"
   (navegación cross-módulo con vistazo en el dropdown) · MÓDULO = "muéstrame
   los que cumplen X" (filtrado). Deep-link F3: un pedido navega con recordId y
   Pedidos lo abre seleccionado en su inspector; productos/clientes se suman al
   migrar en F4 (mientras tanto navegan al módulo, como siempre). */
export default function GlobalSearch({ open, onClose, products = [], catalogPartial = false, onNavigate }) {
  const [query,        setQuery]        = useState('');
  const [prodResults,  setProdResults]  = useState([]);
  const [orderResults, setOrderResults] = useState([]);
  const [custResults,  setCustResults]  = useState([]);
  const [loadingApi,   setLoadingApi]   = useState(false);
  const [focusedIdx,   setFocusedIdx]   = useState(-1);
  const [apiStatus,    setApiStatus]    = useState(ALL_OK);

  const inputRef = useRef(null);
  const listRef  = useRef(null);
  const cacheRef = useRef({});

  // Reset + focus on open.
  // `cacheRef` va acá adentro: `if (!open) return null` NO desmonta el componente —App lo
  // mantiene montado— así que la ref sobrevivía al cierre y la caché vivía lo que vive la
  // pestaña, mientras todo lo demás sí se reseteaba. El modal parecía limpio y no lo estaba.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setProdResults([]);
    setOrderResults([]);
    setCustResults([]);
    setFocusedIdx(-1);
    setApiStatus(ALL_OK);
    cacheRef.current = {};
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  // Product search — instant, in-memory
  useEffect(() => {
    setProdResults(filterProducts(products, query));
    setFocusedIdx(-1);
  }, [query, products]);

  // Orders + customers — debounced API, cached per query string.
  // SÓLO se cachean respuestas completas: un 403 se colapsaba a [] y quedaba fijado para esa
  // query toda la sesión, indistinguible de «no hay resultados» y sin reintentar nunca.
  useEffect(() => {
    const q = query.trim();
    if (!q) { setOrderResults([]); setCustResults([]); setApiStatus(ALL_OK); return; }

    if (cacheRef.current[q]) {
      setOrderResults(cacheRef.current[q].orders);
      setCustResults(cacheRef.current[q].customers);
      setApiStatus(ALL_OK);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingApi(true);
      try {
        const [orRes, cuRes] = await Promise.all([
          fetch(`${API_URL}/api/orders?search=${encodeURIComponent(q)}&per_page=${MAX_PER_GROUP}`),
          fetch(`${API_URL}/api/customers?search=${encodeURIComponent(q)}&per_page=${MAX_PER_GROUP}`),
        ]);
        const status = { orders: sectionStatus(orRes), customers: sectionStatus(cuRes) };
        const orData = orRes.ok ? await orRes.json() : {};
        const cuData = cuRes.ok ? await cuRes.json() : {};
        const orders    = (orData.orders    || []).slice(0, MAX_PER_GROUP);
        const customers = (cuData.customers || []).slice(0, MAX_PER_GROUP);
        if (status.orders === 'ok' && status.customers === 'ok') {
          cacheRef.current[q] = { orders, customers };
        }
        setApiStatus(status);
        setOrderResults(orders);
        setCustResults(customers);
      } catch {
        setOrderResults([]);
        setCustResults([]);
        setApiStatus({ orders: 'error', customers: 'error' });
      } finally {
        setLoadingApi(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Flat list for keyboard navigation
  const allItems = useMemo(() => [
    ...prodResults.map((r)  => ({ ...r, _dest: 'catalog'   })),
    ...orderResults.map((r) => ({ ...r, _dest: 'orders'    })),
    ...custResults.map((r)  => ({ ...r, _dest: 'customers' })),
  ], [prodResults, orderResults, custResults]);

  const prodStart  = 0;
  const orderStart = prodResults.length;
  const custStart  = prodResults.length + orderResults.length;

  // ↑↓ / Enter keyboard nav
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIdx((i) => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && focusedIdx >= 0 && allItems[focusedIdx]) {
      e.preventDefault();
      const item = allItems[focusedIdx];
      onNavigate(item._dest, item._dest === 'orders' ? { recordId: item.id } : null);
      onClose();
    }
  }, [allItems, focusedIdx, onNavigate, onClose]);

  // Auto-scroll focused item into view
  useEffect(() => {
    if (focusedIdx < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-gs-item]');
    items[focusedIdx]?.scrollIntoView({ block: 'nearest' });
  }, [focusedIdx]);

  const handleSelect = useCallback((dest, intent = null) => {
    onNavigate(dest, intent);
    onClose();
  }, [onNavigate, onClose]);

  const hasQuery     = query.trim().length > 0;
  const totalResults = allItems.length;
  // Lo que el backend no dejó buscar. Nombrarlo ES el arreglo: una sección que no se dibuja
  // y nadie explica se lee como «no hay resultados», que es justo lo que no queremos decir.
  const deniedSections = sectionsWith(apiStatus, 'denied');
  const failedSections = sectionsWith(apiStatus, 'error');
  const apiFailed      = failedSections.length > 0;

  if (!open) return null;

  return (
    <div className="gs-overlay" onMouseDown={onClose}>
      <div className="gs-modal" onMouseDown={(e) => e.stopPropagation()}>

        {/* ── Input ───────────────────────────────── */}
        <div className="gs-input-row">
          <svg className="gs-icon-search" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            className="gs-input"
            type="text"
            placeholder="Buscar productos, pedidos, clientes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button className="gs-clear-btn" onClick={() => { setQuery(''); inputRef.current?.focus(); }}>
              ✕
            </button>
          )}
          <kbd className="gs-esc-badge">Esc</kbd>
        </div>

        {/* ── Results ─────────────────────────────── */}
        <div className="gs-body" ref={listRef}>

          {/* Idle state */}
          {!hasQuery && (
            <div className="gs-state gs-state--idle">
              <span className="gs-state-icon">🔍</span>
              <span className="gs-state-title">Busca en todo el dashboard</span>
              <span className="gs-state-sub">Productos · Pedidos · Clientes</span>
            </div>
          )}

          {/* No results — sólo si de verdad se pudo buscar */}
          {hasQuery && !loadingApi && totalResults === 0 && !apiFailed && (
            <div className="gs-state">
              <span className="gs-state-icon">😕</span>
              {/* Con una sección denegada, «No encontramos nada» sería una afirmación de
                  exhaustividad que no se puede sostener: no se buscó en todo. */}
              <span className="gs-state-title">
                {deniedSections.length > 0
                  ? <>Nada para <strong>"{query}"</strong> en lo que puedes buscar</>
                  : <>No encontramos nada para <strong>"{query}"</strong></>}
              </span>
            </div>
          )}

          {/* La búsqueda falló: no es lo mismo que no haber encontrado nada */}
          {hasQuery && !loadingApi && apiFailed && (
            <div className="gs-state">
              <span className="gs-state-icon">⚠️</span>
              <span className="gs-state-title">No pudimos buscar {listar(failedSections)}</span>
              <span className="gs-state-sub">Sigue escribiendo para reintentar.</span>
            </div>
          )}

          {/* ── Productos ─────────────────────────── */}
          {prodResults.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-hd">
                📦 Productos
                {catalogPartial && <span className="gs-group-note"> · el catálogo aún carga — resultados parciales</span>}
              </div>
              {prodResults.map((p, i) => (
                <button
                  key={p.id}
                  data-gs-item
                  className={`gs-item${focusedIdx === prodStart + i ? ' gs-item--on' : ''}`}
                  onMouseEnter={() => setFocusedIdx(prodStart + i)}
                  onMouseLeave={() => setFocusedIdx(-1)}
                  onClick={() => handleSelect('catalog')}
                >
                  {p.image
                    ? <ImagenProducto className="gs-thumb" src={p.image} srcset={p.imageSrcset} sizes="miniatura" phWidth={40} alt="" />
                    : <div className="gs-thumb gs-thumb--ph">SH</div>
                  }
                  <div className="gs-item-info">
                    <span className="gs-item-name">{p.name}</span>
                    <span className="gs-item-sub">
                      {p.categories.join(', ') || '—'}
                      {' · '}
                      {p.manage_stock === false
                        ? 'Sin gestión de stock'
                        : p.stock_quantity > 0
                          ? `${p.stock_quantity} en stock`
                          : 'Agotado'}
                    </span>
                  </div>
                  <span className="gs-item-right">
                    {p.price
                      ? `$${parseFloat(p.price).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`
                      : '—'}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* ── Pedidos ───────────────────────────── */}
          {orderResults.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-hd">🛍️ Pedidos</div>
              {orderResults.map((o, i) => (
                <button
                  key={o.id}
                  data-gs-item
                  className={`gs-item${focusedIdx === orderStart + i ? ' gs-item--on' : ''}`}
                  onMouseEnter={() => setFocusedIdx(orderStart + i)}
                  onMouseLeave={() => setFocusedIdx(-1)}
                  onClick={() => handleSelect('orders', { recordId: o.id })}
                >
                  <div className="gs-order-dot">#{o.number}</div>
                  <div className="gs-item-info">
                    <span className="gs-item-name">
                      {[o.billing?.first_name, o.billing?.last_name].filter(Boolean).join(' ') || '—'}
                    </span>
                    <span className="gs-item-sub">
                      {ORDER_STATUS[o.status] || o.status}
                    </span>
                  </div>
                  <span className="gs-item-right">{fmtMoney(o.total)}</span>
                </button>
              ))}
            </div>
          )}

          {/* ── Clientes ──────────────────────────── */}
          {custResults.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-hd">👤 Clientes</div>
              {custResults.map((c, i) => {
                const displayName = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—';
                return (
                  <button
                    key={c.id}
                    data-gs-item
                    className={`gs-item${focusedIdx === custStart + i ? ' gs-item--on' : ''}`}
                    onMouseEnter={() => setFocusedIdx(custStart + i)}
                    onMouseLeave={() => setFocusedIdx(-1)}
                    onClick={() => handleSelect('customers')}
                  >
                    <div className="gs-avatar">
                      {(displayName[0] || '?').toUpperCase()}
                    </div>
                    <div className="gs-item-info">
                      <span className="gs-item-name">{displayName}</span>
                      <span className="gs-item-sub">
                        {c.cedula ? `CC ${c.cedula}` : c.email || '—'}
                      </span>
                    </div>
                    <span className="gs-item-right">{fmtMoney(c.total_spent)}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* API loading */}
          {loadingApi && hasQuery && (
            <div className="gs-api-loading">
              <span className="gs-spinner" />
              Buscando pedidos y clientes…
            </div>
          )}

          {/* Secciones que el rol no alcanza. La sección NO se dibuja (arriba, por lista vacía);
              esto dice POR QUÉ. Sin esta línea, la ausencia se lee como «no hay resultados». */}
          {hasQuery && !loadingApi && deniedSections.length > 0 && (
            <div className="gs-api-loading" data-gs-denied>
              🔒 Tu rol no incluye la búsqueda de {listar(deniedSections)}.
            </div>
          )}
        </div>

        {/* ── Footer ──────────────────────────────── */}
        <div className="gs-footer">
          <span className="gs-hint"><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
          <span className="gs-hint"><kbd>Enter</kbd> abrir</span>
          <span className="gs-hint"><kbd>Esc</kbd> cerrar</span>
        </div>
      </div>
    </div>
  );
}
